import { GetPublicOverviewResponse } from '@orbit/api-types'

/**
 * A trip member as the public surface knows one. The detail endpoints carry
 * only user *ids*, so the read-only pages resolve names/avatars from the
 * overview's `travelers`, already cached from the tile clicked through.
 */
type PublicTraveler = GetPublicOverviewResponse['travelers'][number]

type TravelerLookup = (userId: number) => PublicTraveler | undefined

/**
 * Index the overview travelers by id for O(1) name/avatar resolution. Unknown
 * ids resolve to `undefined` (renderers fall back to a placeholder); on a
 * duplicate id — which the endpoint never sends — the last entry wins.
 */
export const buildTravelerLookup = (travelers: PublicTraveler[]): TravelerLookup => {
	const byId = new Map<number, PublicTraveler>(travelers.map(t => [t.id, t]))
	return (userId: number) => byId.get(userId)
}

/**
 * Full display name for a traveler, or `null` when the traveler is unknown or
 * carries no usable name — callers omit the row/line instead of rendering a
 * placeholder.
 */
export const travelerFullName = (traveler: PublicTraveler | undefined): string | null => {
	if (!traveler) return null
	return [traveler.firstName, traveler.lastName].filter(Boolean).join(' ') || null
}
